import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { MdOutlineDescription, MdOutlineFileDownload, MdOutlineVerifiedUser } from "react-icons/md";
import { getContractAPI } from "services/user-controllers/onboardingController";
import ContractAcceptance from "./ContractAcceptance";
import ContractDocument from "./ContractDocument";
import "../onboarding/Onboarding.scss";
import "./MyContract.scss";

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "";

// "My Contract": the master customer logistics agreement filled with the customer's details and
// the agreed rate card; the customer signs it and uploads the signed copy for MV Load to verify.
const MyContract = () => {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await getContractAPI();
      if (data?.code === 200) setContract(data.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load your contract");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <div className="ob_page">Loading…</div>;

  if (!contract?.isLead || !contract.available) {
    return (
      <div className="ob_page">
        <div className="ob_title">
          <div>
            <h3>My Contract</h3>
            <p>Your Master Customer Logistics Agreement</p>
          </div>
        </div>
        <div className="ob_card ob_center">
          <h4>Your contract is not ready yet</h4>
          <p>
            It is prepared as soon as your rates are locked. You can review your rates on the{" "}
            <Link to="/user/dashboard/standard-rates">Commercials &amp; TAT</Link> page.
          </p>
        </div>
      </div>
    );
  }

  const step = contract.leadStep;
  const signed = contract.signed || {};
  const banner =
    step === "live"
      ? { tone: "ok", title: "Contract Verified — You're Onboarded & Live!", text: `Your contract was verified${signed.verifiedAt ? ` on ${fmt(signed.verifiedAt)}` : ""}. Welcome to MV Load!`, pill: "Live ✓" }
      : step === "verification"
      ? { tone: "warn", title: signed.method === "otp" ? "Contract Accepted — Verification Pending" : "Signed Contract Uploaded — Verification Pending", text:
          signed.method === "otp"
            ? `You accepted the standard contract via email OTP${signed.otpAcceptedAt ? ` on ${fmt(signed.otpAcceptedAt)}` : ""}. MV Load will verify it shortly.`
            : `We received your signed copy${signed.uploadedAt ? ` on ${fmt(signed.uploadedAt)}` : ""}. MV Load will verify it shortly.`, pill: "Pending" }
      : { tone: "warn", title: "Your Contract is Ready — Please Sign and Upload", text: "Review the agreement below, then accept it with an email OTP or upload your signed copy.", pill: "Action needed" };

  return (
    <div className="ob_page">
      <div className={`ob_lock_banner ${banner.tone}`}>
        <span className="icon">
          <MdOutlineVerifiedUser />
        </span>
        <div className="text">
          <strong>{banner.title}</strong>
          <p>{banner.text}</p>
        </div>
        <span className="pill">{banner.pill}</span>
      </div>

      <div className="ct_card">
        <div className="ct_bar">
          <span className="ct_bar_icon">
            <MdOutlineDescription />
          </span>
          <div className="ct_bar_text">
            <strong>Master Customer Logistics Agreement</strong>
            <span>{contract.effectiveDate ? `Effective ${contract.effectiveDate}` : "Effective date is set when MV Load approves the contract"}</span>
          </div>
          <span className="ct_bar_pill">Charges Locked ✓</span>
          <button type="button" className="ct_bar_btn" onClick={() => window.print()}>
            <MdOutlineFileDownload /> Download PDF
          </button>
        </div>
        <div className="ct_paper">
          <div className="ct_print">
            <ContractDocument contract={contract} />
          </div>
        </div>
      </div>

      {step === "contract-creation" && <ContractAcceptance email={contract.email} onDone={load} />}

      {signed.fileUrl && (
        <div className="ob_card">
          <div className="ob_card_head">
            <h5>Your signed copy</h5>
          </div>
          <a href={signed.fileUrl} target="_blank" rel="noreferrer">
            {signed.fileName || "View signed contract"}
          </a>
        </div>
      )}
    </div>
  );
};

export default MyContract;
